import { Alert, Button, Checkbox, DatePicker, Empty, Input, Modal, Spin, Tag, message as antdMessage } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { map } from 'rxjs/operators';

import { ChannelMessageWithCollection, observeAnnotatedChannelMessages } from 'src/utils/channel/channel-message-util';
import { dayjsesToStringDateWhere, getDefaultDates } from 'src/utils/date-util';
import { QueryOptions, WHERE } from 'src/utils/firebase/firebase-manager';
import {
  SelectedMessageRef,
  getMessageBodyPreview,
  getStoreChannelId,
  toSelectedMessageRef,
} from 'src/utils/issue/issue-case-message-link-util';

import classes from './MessageLinkPickerModal.module.scss';

const { RangePicker } = DatePicker;

interface MessageLinkPickerModalProps {
  open: boolean;
  onClose: () => void;
  storeId: string;
  storeName?: string;
  /** 이미 연결된 messageId 들 — 검색 결과에서 "연결됨" 표시 + 체크 비활성 */
  alreadyLinkedMessageIds: Set<string>;
  /** 사용자가 메시지 + 메모 확정 시 호출. 모달은 호출 측이 닫는다. */
  onConfirm: (selected: SelectedMessageRef[], note: string | null) => Promise<void> | void;
}

const PAGE_LIMIT = 100;
const defaultRange = getDefaultDates(30, 0);

const MessageLinkPickerModal: FC<MessageLinkPickerModalProps> = ({
  open,
  onClose,
  storeId,
  storeName,
  alreadyLinkedMessageIds,
  onConfirm,
}) => {
  const [dates, setDates] = useState<[Dayjs, Dayjs]>(defaultRange);
  const [keyword, setKeyword] = useState('');
  const [note, setNote] = useState('');
  const [messages, setMessages] = useState<ChannelMessageWithCollection[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 모달 닫힐 때 상태 초기화
  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      setKeyword('');
      setNote('');
      setDates(defaultRange);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !storeId) return;

    const [startValue, endValue] = dayjsesToStringDateWhere(dates[0], dates[1]);
    const channelId = getStoreChannelId(storeId);

    const wheres: WHERE[] = [['channelId', '==', channelId]];
    const options: QueryOptions = {
      sortKey: 'createdAt',
      orderBy: 'desc',
      startValue,
      endValue,
      limit: PAGE_LIMIT,
    };

    setIsLoading(true);
    const subscription = observeAnnotatedChannelMessages(wheres, options)
      .pipe(
        map((docs) =>
          docs.filter((doc) => doc._timeDelete == null).sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
        ),
      )
      .subscribe({
        next: (data) => {
          setMessages(data);
          setIsLoading(false);
        },
        error: (error) => {
          console.error('[MessageLinkPickerModal] observe error', error);
          setIsLoading(false);
        },
      });

    return () => subscription.unsubscribe();
  }, [open, storeId, dates]);

  const filtered = useMemo(() => {
    const trimmed = keyword.trim().toLowerCase();
    if (!trimmed) return messages;
    return messages.filter((doc) => {
      const body = (doc.body ?? '').toLowerCase();
      const title = (doc.title ?? '').toLowerCase();
      return body.includes(trimmed) || title.includes(trimmed);
    });
  }, [messages, keyword]);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleConfirm = async () => {
    if (selectedIds.size === 0) {
      antdMessage.warning('메시지를 1건 이상 선택해주세요.');
      return;
    }
    const selectedRefs: SelectedMessageRef[] = filtered
      .filter((doc) => selectedIds.has(doc._id))
      .map(toSelectedMessageRef);

    try {
      setIsSubmitting(true);
      await onConfirm(selectedRefs, note.trim() ? note.trim() : null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={storeName ? `근거 메시지 연결 — ${storeName}` : '근거 메시지 연결'}
      open={open}
      onCancel={onClose}
      width={720}
      footer={[
        <Button key='cancel' onClick={onClose} disabled={isSubmitting}>
          취소
        </Button>,
        <Button
          key='submit'
          type='primary'
          onClick={handleConfirm}
          loading={isSubmitting}
          disabled={selectedIds.size === 0}
        >
          {selectedIds.size > 0 ? `${selectedIds.size}건 연결` : '연결'}
        </Button>,
      ]}
      destroyOnClose
    >
      <div className={classes.toolbar}>
        <RangePicker
          value={dates}
          onChange={(val) => {
            if (val?.[0] && val?.[1]) setDates([val[0], val[1]]);
          }}
          allowClear={false}
        />
        <Input.Search
          placeholder='본문/제목 키워드 (클라이언트 필터)'
          allowClear
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
      </div>

      <Alert
        type='info'
        showIcon
        className={classes.infoBanner}
        message={`해당 매장 채널(${getStoreChannelId(storeId)})의 최근 메시지만 표시됩니다. 최대 ${PAGE_LIMIT}건씩 조회.`}
      />

      <div className={classes.listWrapper}>
        {isLoading ? (
          <div className={classes.spinner}>
            <Spin />
          </div>
        ) : filtered.length === 0 ? (
          <Empty description='조건에 맞는 메시지가 없습니다.' />
        ) : (
          <div className={classes.list}>
            {filtered.map((doc) => {
              const alreadyLinked = alreadyLinkedMessageIds.has(doc._id);
              const isSelected = selectedIds.has(doc._id);
              return (
                <div
                  key={doc._id}
                  className={`${classes.row} ${alreadyLinked ? classes.disabledRow : ''} ${
                    isSelected ? classes.selectedRow : ''
                  }`}
                  onClick={() => {
                    if (alreadyLinked) return;
                    toggleSelected(doc._id);
                  }}
                >
                  <Checkbox
                    checked={alreadyLinked || isSelected}
                    disabled={alreadyLinked}
                    onChange={() => toggleSelected(doc._id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className={classes.rowMain}>
                    <div className={classes.rowHeader}>
                      <span className={classes.sender}>
                        {doc.senderCollection}:{doc.senderId}
                      </span>
                      <span className={classes.createdAt}>{dayjs(doc.createdAt).format('YYYY-MM-DD HH:mm')}</span>
                      {alreadyLinked && (
                        <Tag color='blue' className={classes.linkedTag}>
                          연결됨
                        </Tag>
                      )}
                    </div>
                    {doc.title && <div className={classes.title}>{doc.title}</div>}
                    <div className={classes.body}>{getMessageBodyPreview(doc.body, 200) || '(본문 없음)'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Input.TextArea
        placeholder='연결 메모 (선택). 같은 호출에서 묶이는 모든 메시지에 동일하게 저장됩니다.'
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        className={classes.noteInput}
      />
    </Modal>
  );
};

export default MessageLinkPickerModal;
